import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Spinner } from 'react-bootstrap';
import { API_URL } from '../config';
import { readerFetch } from '../apiAuth';
import './BookFullView.css';
import { t } from '../i18n';

// ─────────────────────────────────────────
// OrderModal — модалка вибору пункту видачі + оформлення замовлення
// (order.php?action=points/create). Винесена з BookFullView.js (сесія
// 2026-08-22), щоб її можна було відкрити напряму з «Закладок» (кнопка
// «Замовити» в CabinetPage.js), без переходу на повну картку видання.
//
// Самодостатня — сама викликає action=points при монтуванні (сервер і так
// авторитетне джерело правди для боргу/дублю/поля 56). Єдиний виняток —
// проп initialDebt: коли борг уже відомий клієнту (issuedBooks у картці
// книги, BookFullView.js), модалка одразу показує екран боргу й НЕ смикає
// order.php (замовлення все одно неможливе). Це єдина спільна реалізація
// логіки замовлення — BookFullView.js більше власної копії не тримає.
// ─────────────────────────────────────────

const ORDER_PANEL_ANIM_MS = 220; // синхронізовано з тривалістю CSS-анімації закриття

function OrderModal({ db, mfn, user, lang, trackEvent, initialDebt = false, onOrderPlaced, onOpenCabinetSection, onClose }) {
  const [closing,            setClosing]            = useState(false);
  const [points,             setPoints]             = useState(null); // null = не завантажено
  const [pointsLoading,      setPointsLoading]      = useState(!initialDebt);
  const [duplicate,          setDuplicate]          = useState(false);
  const [restricted,         setRestricted]         = useState(false);
  const [noAllowedPoint,     setNoAllowedPoint]     = useState(false);
  const [debt,               setDebt]               = useState(initialDebt);
  const [duplicateConfirmed, setDuplicateConfirmed] = useState(false);
  const [selectedPickup,     setSelectedPickup]     = useState(null);
  const [submitting,         setSubmitting]         = useState(false);
  const [result,             setResult]             = useState(null); // 'success' | 'error' | null

  const closeTimerRef = useRef(null);
  const readerLoginForLog = user?.login || null;

  useEffect(() => {
    // Борг відомий заздалегідь — один подієвий запис blocked_debt, без плейн-події
    // й без запиту (та сама поведінка, що була в BookFullView.js).
    if (initialDebt) {
      if (trackEvent) trackEvent('order', { db_name: db, mfn, login: readerLoginForLog, detail: 'blocked_debt' });
      return;
    }
    if (trackEvent) trackEvent('order', { db_name: db, mfn, login: readerLoginForLog });
    readerFetch(`${API_URL}/order.php?action=points&db=${db}&mfn=${mfn}`, user?.reader_token)
      .then(r => r.json())
      .then(d => {
        setPoints(d.points || []);
        setDuplicate(!!d.duplicate);
        setRestricted(!!d.restricted);
        setNoAllowedPoint(!!d.noAllowedPoint);
        setDebt(!!d.debt);
        const blockReason = d.debt ? 'blocked_debt' : d.restricted ? 'blocked_restricted' : d.noAllowedPoint ? 'blocked_no_point' : null;
        if (blockReason && trackEvent) trackEvent('order', { db_name: db, mfn, login: readerLoginForLog, detail: blockReason });
      })
      .catch(() => setPoints([]))
      .finally(() => setPointsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [db, mfn]);

  // Блокуємо скрол сторінки, поки модалка відкрита (той самий паттерн, що й у BookFullView.js)
  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prevOverflow; };
  }, []);

  useEffect(() => () => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
  }, []);

  const handleClose = () => {
    const reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion) { onClose(); return; }
    setClosing(true);
    closeTimerRef.current = setTimeout(() => { onClose(); }, ORDER_PANEL_ANIM_MS);
  };

  const submitOrder = () => {
    if (!selectedPickup) return;
    setSubmitting(true);
    readerFetch(`${API_URL}/order.php?action=create`, user?.reader_token, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ db, mfn, pickup: selectedPickup }),
    })
      .then(r => r.json())
      .then(d => {
        setResult(d.success ? 'success' : 'error');
        if (d.success && onOrderPlaced) onOrderPlaced(db, mfn);
      })
      .catch(() => setResult('error'))
      .finally(() => setSubmitting(false));
  };

  const renderBody = () => {
    if (debt) {
      return (
        <div className="bfv-order-panel-result">
          <i className="fa-solid fa-triangle-exclamation" />
          <span>{t(lang, 'book_order_debt')}</span>
          <button
            type="button"
            className="bfv-order-help-link"
            onClick={() => {
              handleClose();
              if (onOpenCabinetSection) onOpenCabinetSection('issued');
            }}
          >
            <i className="fa-solid fa-book-open" />
            {t(lang, 'book_order_debt_issued_btn')}
          </button>
          <button type="button" className="bfv-order-panel-close-btn" onClick={handleClose}>
            {t(lang, 'book_order_ok')}
          </button>
        </div>
      );
    }

    if (result === 'success') {
      return (
        <div className="bfv-order-panel-result bfv-order-panel-result--success">
          <i className="fa-solid fa-circle-check" />
          <span>{t(lang, 'book_order_success')}</span>
          <button type="button" className="bfv-order-panel-close-btn" onClick={handleClose}>
            {t(lang, 'book_order_ok')}
          </button>
        </div>
      );
    }

    if (result === 'error') {
      return (
        <div className="bfv-order-panel-result bfv-order-panel-result--error">
          <i className="fa-solid fa-circle-exclamation" />
          <span>{t(lang, 'book_order_error')}</span>
          <div className="bfv-order-panel-actions">
            <button type="button" className="bfv-order-panel-submit" onClick={submitOrder}>
              {t(lang, 'book_order_confirm')}
            </button>
            <button type="button" className="bfv-order-panel-close-btn" onClick={handleClose}>
              {t(lang, 'book_order_cancel')}
            </button>
          </div>
        </div>
      );
    }

    if (pointsLoading) {
      return (
        <div className="bfv-order-panel-loading">
          <Spinner animation="border" size="sm" />
        </div>
      );
    }

    if (restricted) {
      return (
        <div className="bfv-order-panel-result">
          <i className="fa-solid fa-circle-info" />
          <span>{t(lang, 'book_order_restricted')}</span>
          <a
            href="https://lib.udu.edu.ua/posluhy/dopomoha-onlain"
            target="_blank"
            rel="noopener noreferrer"
            className="bfv-order-help-link"
          >
            <i className="fa-solid fa-headset" />
            {t(lang, 'book_order_help_online')}
          </a>
          <button type="button" className="bfv-order-panel-close-btn" onClick={handleClose}>
            {t(lang, 'book_order_ok')}
          </button>
        </div>
      );
    }

    if (duplicate && !duplicateConfirmed) {
      return (
        <div className="bfv-order-duplicate-notice">
          <i className="fa-solid fa-circle-info" />
          <span>{t(lang, 'book_order_duplicate')}</span>
          <div className="bfv-order-panel-actions">
            <button
              type="button"
              className="bfv-order-panel-submit"
              onClick={() => setDuplicateConfirmed(true)}
            >
              {t(lang, 'book_order_duplicate_confirm')}
            </button>
            <button type="button" className="bfv-order-panel-close-btn" onClick={handleClose}>
              {t(lang, 'book_order_cancel')}
            </button>
          </div>
        </div>
      );
    }

    if (noAllowedPoint) {
      return (
        <div className="bfv-order-panel-result">
          <i className="fa-solid fa-circle-exclamation" />
          <span>{t(lang, 'book_order_no_allowed_point')}</span>
          <button type="button" className="bfv-order-panel-close-btn" onClick={handleClose}>
            {t(lang, 'book_order_ok')}
          </button>
        </div>
      );
    }

    if (!points || points.length === 0) {
      return (
        <div className="bfv-order-panel-result">
          <i className="fa-solid fa-circle-exclamation" />
          <span>{t(lang, 'book_order_none_available')}</span>
          <button type="button" className="bfv-order-panel-close-btn" onClick={handleClose}>
            {t(lang, 'book_order_ok')}
          </button>
        </div>
      );
    }

    return (
      <>
        <div className="bfv-order-panel-points">
          {points.map(p => (
            <label key={p.code} className="bfv-order-panel-point">
              <input
                type="radio"
                name="orderPickup"
                value={p.code}
                checked={selectedPickup === p.code}
                onChange={() => setSelectedPickup(p.code)}
              />
              <span className="bfv-order-panel-point-label">{p.label}</span>
              <span className="bfv-order-panel-point-free">
                {t(lang, 'book_order_free_label')}: {p.free_count}
              </span>
            </label>
          ))}
        </div>
        <div className="bfv-order-panel-actions">
          <button
            type="button"
            className="bfv-order-panel-submit"
            disabled={!selectedPickup || submitting}
            onClick={submitOrder}
          >
            {submitting ? <Spinner animation="border" size="sm" /> : t(lang, 'book_order_confirm')}
          </button>
          <button type="button" className="bfv-order-panel-close-btn" onClick={handleClose}>
            {t(lang, 'book_order_cancel')}
          </button>
        </div>
      </>
    );
  };

  return createPortal(
    <div
      className={`bfv-order-modal-backdrop${closing ? ' bfv-order-modal-backdrop--closing' : ''}`}
      onClick={e => { if (e.target === e.currentTarget) handleClose(); }}
    >
      <div className={`bfv-order-modal${closing ? ' bfv-order-modal--closing' : ''}`} role="dialog" aria-modal="true">
        <div className="bfv-order-modal-header">
          <span>{t(lang, 'book_order_pickup_title')}</span>
          <button
            type="button"
            className="bfv-order-modal-close"
            onClick={handleClose}
            aria-label={t(lang, 'book_order_cancel')}
          >
            <i className="fa-solid fa-xmark" />
          </button>
        </div>
        <div className="bfv-order-modal-body">
          {renderBody()}
        </div>
      </div>
    </div>,
    document.body
  );
}

export default OrderModal;
